import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  fabUserNotifications: [],
  unseenUserNotificationCount: 0,
  highlightedToasts: [],
};

const userNotificationSlice = createSlice({
  name: 'userNotifications',
  initialState,
  reducers: {
    receiveUserNotification: (state, action) => {
      const event = action.payload || {};
      if (!event.eventType && !event.body && !event.title) return;

      const fabKey = [
        event.eventType || 'USER_NOTIFICATION',
        event.sourceMessageId || event.taskId || event.body || event.title,
        event.receivedAt || '',
      ].filter(Boolean).join('-');

      const exists = state.fabUserNotifications.some((n) => n.fabKey === fabKey);
      if (exists) return;

      state.fabUserNotifications.unshift({
        fabKey,
        eventType: event.eventType || 'USER_NOTIFICATION',
        title: event.title || 'Notification',
        body: event.body || event.title || 'Update',
        actorName: event.actorName || '',
        sourceChannelId: event.sourceChannelId || event.channelId || null,
        sourceMessageId: event.sourceMessageId || event.chatMessageId || event.messageId || null,
        taskId: event.taskId || null,
        trackId: event.trackId || null,
        ticketTitle: event.ticketTitle || null,
        notificationChannelId: event.notificationChannelId || null,
        receivedAt: new Date().toISOString(),
      });
      if (state.fabUserNotifications.length > 20) {
        state.fabUserNotifications.length = 20;
      }
      state.unseenUserNotificationCount += 1;
    },
    markUserNotificationsSeen: (state) => {
      state.unseenUserNotificationCount = 0;
    },
    dismissFabUserNotification: (state, action) => {
      const fabKey = action.payload;
      state.fabUserNotifications = state.fabUserNotifications.filter(
        (n) => n.fabKey !== fabKey
      );
    },
    clearAllFabUserNotifications: (state) => {
      state.fabUserNotifications = [];
      state.unseenUserNotificationCount = 0;
    },
    pushHighlightedToast: (state, action) => {
      const event = action.payload || {};
      const subject = String(
        event.taskId
        || event.task?.id
        || event.trackId
        || event.sourceMessageId
        || event.appointmentId
        || event.sourceChannelId
        || event.channelId
        || event.fabKey
        || ''
      ).trim();
      const dedupeKey = `${event.eventType || event.type || 'EVENT'}-${subject || event.body || event.title || Date.now()}`;
      const exists = state.highlightedToasts.some((toast) => toast.dedupeKey === dedupeKey);
      if (exists) return;
      state.highlightedToasts.unshift({
        id: `highlight-${dedupeKey}-${Date.now()}`,
        dedupeKey,
        eventType: event.eventType || event.type || 'USER_NOTIFICATION',
        title: event.title || event.notificationTitle || event.ticketTitle || 'Notification',
        body: event.body || event.notificationBody || event.title || event.ticketTitle || 'Update',
        actorName: event.actorName
          || event.senderName
          || event.delegatorName
          || event.message?.senderName
          || '',
        senderId: event.senderId || event.message?.senderId || null,
        sourceChannelId: event.sourceChannelId || event.channelId || null,
        sourceMessageId: event.sourceMessageId || event.chatMessageId || event.messageId || null,
        taskId: event.taskId || event.task?.id || null,
        trackId: event.trackId || null,
        ticketTitle: event.ticketTitle || event.title || null,
        statusLabel: event.statusLabel || null,
        reactionKey: event.reactionKey || event.reactionEmoji || event.statusLabel || null,
        reactionEmoji: event.reactionEmoji || null,
        thumbnailFileKey: event.thumbnailFileKey
          || event.message?.attachments?.find((item) => item?.fileUrl)?.fileUrl
          || null,
        notificationChannelId: event.notificationChannelId || null,
        appointmentId: event.appointmentId || null,
        meetingLink: event.meetingLink || null,
      });
      if (state.highlightedToasts.length > 20) {
        state.highlightedToasts.length = 20;
      }
    },
    dismissHighlightedToastsByKeys: (state, action) => {
      const keys = new Set((action.payload || []).map((key) => String(key)));
      if (!keys.size) return;
      state.highlightedToasts = state.highlightedToasts.filter((toast) => {
        const toastKeys = [
          toast.taskId,
          toast.trackId,
          toast.sourceMessageId,
          toast.appointmentId,
          toast.sourceChannelId,
        ].map((key) => String(key || '')).filter(Boolean);
        return !toastKeys.some((key) => keys.has(key));
      });
    },
    dismissHighlightedToast: (state, action) => {
      state.highlightedToasts = state.highlightedToasts.filter((toast) => toast.id !== action.payload);
    },
    clearHighlightedToasts: (state) => {
      state.highlightedToasts = [];
    },
  },
});

export const {
  receiveUserNotification,
  markUserNotificationsSeen,
  dismissFabUserNotification,
  clearAllFabUserNotifications,
  pushHighlightedToast,
  dismissHighlightedToast,
  clearHighlightedToasts,
  dismissHighlightedToastsByKeys,
} = userNotificationSlice.actions;

export default userNotificationSlice.reducer;
